import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../../api/client';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow } from '../../../components/confirm';
import {
  ContainersPopup, ContainersTab, ParameterPickerDialog, TestAssignmentDialog,
  fetchBottleLookups, fetchTestParams,
} from '../../sampling/sampleRegistration/SampleLoginView';
import { copyTest, type BottleLookups, type SrSample, type SrTest } from '../../sampling/sampleRegistration/srData';
import { PRE_LOGIN_FORM } from './PreLoginSettingsDetailPage';
import {
  applyGuide, bottleSignature, bottlesToInputs, buildSrSamples, restoreParams,
  srTestKey, testsToInputs, type PreLoginSrModel,
} from './preLoginSr';
import type {
  PreLoginBottle, PreLoginParameter, PreLoginSample, PreLoginTest, PreLoginTestCatalogRow,
} from './types';

type GroupChildren = Record<string, { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string }[]>;

interface LookupRow { lookupKey: string; col1: string; col2: string; col3: string; col4: string; col5?: string; col6?: string }

/** Sample Registration's own test list + group members + units, fetched once. */
interface RegLookups {
  /** [name, method, isGroup, component, isFieldTest, oid] — Registration's row shape. */
  testRows: string[][];
  groupChildren: GroupChildren;
  units: string[];
  /** `name|method|component` (lower-case) of every FIELD test. */
  fieldKeys: Set<string>;
}

let _regLookups: Promise<RegLookups> | null = null;
function loadRegLookups(): Promise<RegLookups> {
  if (_regLookups) return _regLookups;
  _regLookups = api.get('/api/v1/sample-registration/lookups').then(res => {
    const rows: LookupRow[] = res.data?.data ?? [];
    const testRows: string[][] = [];
    const groupChildren: GroupChildren = {};
    const units: string[] = [];
    const fieldKeys = new Set<string>();
    for (const r of rows) {
      if (r.lookupKey === 'test') {
        testRows.push([r.col1 ?? '', r.col2 ?? '', r.col3 ?? '', r.col4 ?? '', r.col6 ?? '', r.col5 ?? '']);
        if (r.col6 === '1') fieldKeys.add(`${(r.col1 ?? '').trim().toLowerCase()}|${(r.col2 ?? '').trim().toLowerCase()}|${((r.col4 ?? '') || 'Default').trim().toLowerCase()}`);
      } else if (r.lookupKey === 'testgroup' && r.col1) {
        (groupChildren[r.col1] ??= []).push({
          name: r.col2 ?? '', method: r.col3 ?? '', comp: r.col4 ?? '',
          isFieldTest: r.col6 === '1' || undefined, oid: r.col5 ?? '',
        });
      } else if (r.lookupKey === 'unit' && r.col1) {
        units.push(r.col1);
      }
    }
    return { testRows, groupChildren, units, fieldKeys };
  }).catch(e => { _regLookups = null; throw e; });
  return _regLookups;
}

const apiMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? fallback;

export interface PreLoginSrArgs {
  cocOid: string;
  cocId: string | null;
  samples: PreLoginSample[];
  tests: PreLoginTest[];
  bottles: PreLoginBottle[];
  params: PreLoginParameter[];
  /** True while Samples has unsaved buffered edits — server writes are refused. */
  dirty: boolean;
  setActiveSample: (oid: string) => void;
  /** Re-reads the COC's samples + tests + bottles + parameters. */
  onChanged: () => Promise<{ tests: PreLoginTest[]; bottles: PreLoginBottle[]; params: PreLoginParameter[] }>;
  /** Writes new Qty values (sample Oid → qty) through the samples PUT. */
  persistQty: (qty: Record<string, number>) => Promise<void>;
}

/**
 * Drives Registration's Test Assignment window, Containers window / tab and
 * parameter checklist for a Pre-Login COC. See {@link buildSrSamples} for the
 * model translation; this hook owns loading the supporting lookups and
 * persisting what those components change through the existing, audited
 * whole-set endpoints (tests PUT / bottles PUT / samples PUT for Qty).
 */
export function usePreLoginSr(a: PreLoginSrArgs) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [reg, setReg] = useState<RegLookups | null>(null);
  const [bl, setBl] = useState<BottleLookups | null>(null);
  const [paramVer, setParamVer] = useState(0);

  const [testFor, setTestFor] = useState<{ sampleOid: string; rows: string[][] } | null>(null);
  const [contFor, setContFor] = useState<{ sampleOid: string; testKey: string | null } | null>(null);
  const [paramFor, setParamFor] = useState<{ sampleOid: string; testKey: string } | null>(null);

  useEffect(() => {
    let alive = true;
    loadRegLookups().then(r => { if (alive) setReg(r); })
      .catch(() => { if (alive) notify.error('Could not load the test list.'); });
    return () => { alive = false; };
  }, [notify]);

  // Bottle lookups scoped to this COC's tests, so the Test Guide pairs resolve.
  const tmOids = useMemo(
    () => [...new Set(a.tests.map(t => t.testMethodOid).filter((x): x is string => !!x))].sort().join(','),
    [a.tests]);
  useEffect(() => {
    let alive = true;
    fetchBottleLookups(tmOids || undefined).then(b => { if (alive) setBl(b); });
    return () => { alive = false; };
  }, [tmOids]);

  const model: PreLoginSrModel = useMemo(
    () => buildSrSamples(a.samples, a.tests, a.bottles, a.params, reg?.fieldKeys ?? new Set()),
    [a.samples, a.tests, a.bottles, a.params, reg]);

  // Analyte lists feed the Parameter label / customised-selection restore.
  useEffect(() => {
    let alive = true;
    const need = model.samples.flatMap(s => s.tests);
    if (need.length === 0) return;
    void Promise.all(need.map(t => fetchTestParams(t))).then(() => { if (alive) setParamVer(v => v + 1); });
    return () => { alive = false; };
  }, [model]);

  /** What the Registration components see: customised analytes restored and the
   *  Test Guide's top Container / Preservative pre-filled for blank bottles. */
  const srSamples: SrSample[] = useMemo(() => {
    const restored = restoreParams(model.samples);
    return bl ? applyGuide(restored, bl) : restored;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, bl, paramVer]);

  const sampleOf = (oid: string) => srSamples.find(s => model.origin.sampleOidById.get(s.id) === oid);
  /**
   * The CONTAINERS window is the only one that has to refuse a dirty grid. Saving
   * bottles can carry a Qty change, and Qty is written through the SAMPLES endpoint
   * (`persistQty`) from the LOADED rows — so running it over pending cell edits
   * would write them away. The Test Assignment and Parameter windows save through
   * the per-sample tests endpoint and only reload the children, which leaves the
   * buffered sample edits untouched; they used to refuse too, which just blocked
   * the Test link for no reason (owner report 2026-11-03).
   */
  const refuseIfDirty = (): boolean => {
    if (a.dirty) { notify.warning('Save the samples first, then continue.'); return true; }
    return false;
  };

  // ── bottles (and Qty) ────────────────────────────────────────────────────
  /** Writes whichever samples differ from what is stored; returns when done. */
  const writeBottles = useCallback(async (next: SrSample[], blNow: BottleLookups) => {
    const qty: Record<string, number> = {};
    for (const s of next) {
      const oid = model.origin.sampleOidById.get(s.id);
      const before = srSamples.find(x => x.id === s.id);
      if (!oid || !before) continue;
      if (bottleSignature(before) === bottleSignature(s)) continue;
      if (before.v.Qty !== s.v.Qty) qty[oid] = Math.max(1, parseInt(s.v.Qty ?? '1', 10) || 1);
      await api.put(`/api/v1/pre-login-settings/samples/${oid}/bottles`,
        bottlesToInputs(s, oid, model.origin, blNow));
    }
    if (Object.keys(qty).length) await a.persistQty(qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, srSamples, a.persistQty]);

  const persistBottles = useCallback((next: SrSample[]) => {
    if (!bl || refuseIfDirty()) return;
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit',
      save: async () => {
        try { await writeBottles(next, bl); }
        catch (e) { notify.error(apiMsg(e, 'Could not save the container changes.')); throw e; }
      },
      onSaved: () => { void a.onChanged(); },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bl, writeBottles, runSave, a.onChanged, a.dirty]);

  // ── tests ────────────────────────────────────────────────────────────────
  /** Saves one sample's tests, then lets the Test Guide fill the new bottles. */
  const saveTests = useCallback((sampleOid: string, next: SrTest[], close: () => void) => {
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit',
      save: async () => {
        try {
          await api.put(`/api/v1/pre-login-settings/samples/${sampleOid}/tests`,
            testsToInputs(sampleOid, next, model.origin));
        } catch (e) { notify.error(apiMsg(e, 'Could not save the tests.')); throw e; }
        // The server regenerates blank bottle rows for the new tests; fill them from
        // the Test Guide the way Registration does, so Container / Preservative are
        // never left blank after an assignment.
        try {
          const fresh = await a.onChanged();
          const oids = [...new Set(fresh.tests.map(t => t.testMethodOid).filter((x): x is string => !!x))].sort().join(',');
          const blNow = await fetchBottleLookups(oids || undefined);
          const m = buildSrSamples(a.samples, fresh.tests, fresh.bottles, fresh.params, reg?.fieldKeys ?? new Set());
          const one = m.samples.find(s => m.origin.sampleOidById.get(s.id) === sampleOid);
          if (one) {
            const filled = applyGuide([one], blNow)[0]!;
            if (bottleSignature(filled) !== bottleSignature(one)) {
              await api.put(`/api/v1/pre-login-settings/samples/${sampleOid}/bottles`,
                bottlesToInputs(filled, sampleOid, m.origin, blNow));
            }
          }
        } catch (e) { notify.warning(apiMsg(e, 'Tests saved, but the container defaults could not be applied.')); }
      },
      onSaved: () => { close(); void a.onChanged(); },
    });
  }, [runSave, model, a, reg, notify]);

  // ── openers ──────────────────────────────────────────────────────────────
  const openTest = useCallback(async (sampleOid: string) => {
    if (!reg) return;
    a.setActiveSample(sampleOid);
    // Registration lists every test; scope to this sample's matrix with the same
    // server-side catalogue the sample's Add picker used (two-hop VisualMatrix walk).
    let rows = reg.testRows;
    try {
      const res = await api.get(`/api/v1/pre-login-settings/samples/${sampleOid}/test-catalog`);
      const cat: PreLoginTestCatalogRow[] = res.data?.data ?? [];
      const keys = new Set(cat.map(c =>
        `${(c.test ?? '').trim().toLowerCase()}|${(c.method ?? '').trim().toLowerCase()}|${(c.component || 'Default').trim().toLowerCase()}`));
      const scoped = reg.testRows.filter(r =>
        keys.has(`${r[0]!.trim().toLowerCase()}|${r[1]!.trim().toLowerCase()}|${(r[3] || 'Default').trim().toLowerCase()}`));
      if (scoped.length > 0) rows = scoped;
    } catch { /* unscoped list is still usable */ }
    setTestFor({ sampleOid, rows });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reg, a.setActiveSample]);

  const openCont = useCallback((sampleOid: string, testKey?: string | null) => {
    if (refuseIfDirty()) return;
    a.setActiveSample(sampleOid);
    setContFor({ sampleOid, testKey: testKey ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.dirty, a.setActiveSample]);

  const openParams = useCallback((sampleOid: string, testKey: string) => {
    setParamFor({ sampleOid, testKey });
  }, []);

  // ── dialogs ──────────────────────────────────────────────────────────────
  const dialogs: ReactNode = (<>
    {testFor && reg && (() => {
      const s = sampleOf(testFor.sampleOid);
      if (!s) return null;
      const matrix = a.tests.find(t => t.sampleOid === testFor.sampleOid)?.matrix
        ?? a.samples.find(x => x.oid === testFor.sampleOid)?.sampleMatrix ?? '';
      return (
        <TestAssignmentDialog
          sample={s} matrixName={matrix ?? ''}
          testRows={testFor.rows} groupChildren={reg.groupChildren}
          onClose={() => setTestFor(null)} onWarn={m => notify.warning(m)}
          onSave={nextTests => saveTests(testFor.sampleOid, nextTests, () => setTestFor(null))}
        />
      );
    })()}

    {contFor && bl && (() => {
      const s = sampleOf(contFor.sampleOid);
      if (!s) return null;
      return (
        <ContainersPopup
          samples={srSamples} clickedId={s.id} jobId={a.cocId ?? ''}
          initialTestKey={contFor.testKey}
          setSamples={persistBottles}
          onClose={() => setContFor(null)}
          onSuccess={() => { /* the shared save flow acknowledges the save */ }}
          onWarn={m => notify.warning(m)}
        />
      );
    })()}

    {paramFor && (() => {
      const s = sampleOf(paramFor.sampleOid);
      const t = s?.tests.find(x => x.key === paramFor.testKey);
      if (!s || !t) return null;
      return (
        <ParameterPickerDialog
          test={t} unitOptions={reg?.units}
          onClose={() => setParamFor(null)}
          onApply={params => {
            const next = s.tests.map(x => {
              if (x.key !== t.key) return x;
              const c = copyTest(x);
              c.params = params;
              return c;
            });
            saveTests(paramFor.sampleOid, next, () => setParamFor(null));
          }}
        />
      );
    })()}
  </>);

  return { srSamples, bl, openTest, openCont, openParams, persistBottles, dialogs, ready: !!reg && !!bl };
}

/**
 * The Containers TAB — Registration's own ContainersTab (All Containers / By
 * Sample, editable Container / Preservative / Storage, the bottle picker, Assign /
 * Copy / Reset Bottles) over a working copy, with Save / Cancel changes. The edits
 * are buffered exactly as in Registration and written together on Save.
 */
export function PreLoginContainersPanel({ srSamples, activeSample, cocId, onPersist, onWarn, sampleOidOf }: {
  srSamples: SrSample[];
  activeSample: string | null;
  cocId: string | null;
  onPersist: (next: SrSample[]) => void;
  onWarn: (m: string) => void;
  sampleOidOf: (id: string) => string | undefined;
}) {
  const [draft, setDraft] = useState<SrSample[]>(srSamples);
  const [active, setActive] = useState(0);
  const base = useRef(srSamples);

  // A reload (or a save) replaces the baseline; take it unless edits are in flight.
  useEffect(() => { base.current = srSamples; setDraft(srSamples); }, [srSamples]);
  useEffect(() => {
    const i = srSamples.findIndex(s => sampleOidOf(s.id) === activeSample);
    if (i >= 0) setActive(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSample]);

  const dirty = useMemo(
    () => draft.some((s, i) => base.current[i] && bottleSignature(base.current[i]!) !== bottleSignature(s)),
    [draft]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 6 }}>
        <button className="ca-btn" disabled={!dirty} onClick={() => setDraft(base.current)}>Cancel changes</button>
        <button className="ca-btn ca-btn-solid" disabled={!dirty} onClick={() => onPersist(draft)}>Save changes</button>
      </div>
      <ContainersTab
        samples={draft} setSamples={setDraft} active={active} setActive={setActive}
        onSuccess={() => { /* the shared save flow acknowledges the save */ }}
        onWarn={onWarn} jobId={cocId ?? ''}
      />
    </div>
  );
}

export { srTestKey };
